import { IEditCommand, IEditCommandFactory, IEditEntityCommandStorable } from "./command";
import { ISourceReferable } from "./ISourceReferable";
import { IPropertyPresentable } from "./IPropertyPresentable";
import { IPropertyEditable } from "./IPropertyEditable";
import { IPropertyPresenterCreatable } from "./IPropertyPresenterCreatable";
import { IPropertyEditorCreatable } from "./IPropertyEditorCreatable";
import { AddEntityCommandFactory } from "./AddEntityCommandFactory";
import { EditEntityCommandStore } from "./EditEntityCommandStore";

export abstract class AbstractEntityEditor<TSource, TEntityBase, TEntity extends TEntityBase> implements IEditCommandFactory<TSource>, IEditEntityCommandStorable<TEntity>, ISourceReferable<TEntity>, IPropertyPresenterCreatable<TEntity>, IPropertyEditorCreatable<TEntity> {
  constructor(source: TEntity | AbstractEntityEditor<TSource, TEntityBase, TEntity>, isNew: boolean = false) {
    if (source instanceof AbstractEntityEditor) {
      this._commandFactory = source._commandFactory;
    } else {
      this._commandFactory = isNew
        ? new AddEntityCommandFactory(source, s => this.toEntities(s))
        : new EditEntityCommandStore(source, (s, e) => this.toEntity(s, e));
    }
  }

  private readonly _commandFactory: IEditCommandFactory<TSource> & IEditEntityCommandStorable<TEntity> & ISourceReferable<TEntity> & IPropertyPresenterCreatable<TEntity> & IPropertyEditorCreatable<TEntity>;

  protected isNew(): boolean {
    return !!(this._commandFactory instanceof AddEntityCommandFactory);
  }

  protected abstract toEntities(source: TSource): TEntityBase[];

  protected abstract toEntity(source: TSource, entity: TEntity): TEntity;

  public createCommand(): IEditCommand<TSource> {
    return this._commandFactory.createCommand();
  }

  public registerEntityCommand(command: IEditCommand<TEntity>): void {
    this._commandFactory.registerEntityCommand(command);
  }

  public getSource(): TEntity {
    return this._commandFactory.getSource();
  }

  public isReferedSameSource(instance: ISourceReferable<TEntity>): boolean {
    return !!instance && (this.getSource() === instance.getSource());
  }

  protected get<T>(get: (s: TEntity) => T, defaultValue: T): T {
    return get(this._commandFactory.getSource());
  }

  public createPropertyPresenter<T>(get: (s: TEntity) => T): IPropertyPresentable<T> {
    return this._commandFactory.createPropertyPresenter(get);
  }

  public createPropertyEditor<T>(get: (s: TEntity) => T, set: (s: TEntity, v: T) => void, isSettable?: (s: TEntity) => boolean): IPropertyEditable<T> {
    return this._commandFactory.createPropertyEditor(get, set, isSettable);
  }

  public createPropertiesEditor<TValue, TValues>(get: (s: TEntity) => TValues, toValue: (vs: TValues) => TValue, set: (s: TEntity, v: TValue) => void, revert: (s: TEntity, vs: TValues) => void, isSettable?: (s: TEntity) => boolean): IPropertyEditable<TValue> {
    return this._commandFactory.createPropertiesEditor(get, toValue, set, revert, isSettable);
  }
}
